import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AchterdeurOptionsPanel from '../components/AchterdeurOptionsPanel';
import DoorDrawingBoard from '../components/DoorDrawingBoard';
import Summary from '../components/Summary';
import Quote from '../components/Quote';
import SavedQuotes from '../components/SavedQuotes';
import { useTranslation } from '../context/TranslationContext';
import { 
  raamtypes, 
  profileSystems, 
  glassTextures, 
  glazingTypes, 
  colors, 
  woodJointOptions,
  PRICING 
} from '../data/mockData';

const Achterdeur = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showSummary, setShowSummary] = useState(true);
  const [showQuote, setShowQuote] = useState(false);
  const [showSavedQuotes, setShowSavedQuotes] = useState(false);
  const drawingBoardRef = useRef(null);
  
  const [config, setConfig] = useState({
    width: 800,
    height: 2300,
    profileSystem: 'synego-1',
    thresholdHeight: '30mm',
    handlePosition: 'left',
    topLight: 'no-top-light',
    numberOfParts: 'one-part',
    panelType: 'sandwich-no-mullions', // Default to Sandwich Panel when Two Parts is selected
    glassTexture: 'helder-glas',
    glazingType: 'double',
    security: 'none',
    interiorColor: 'white',
    exteriorColor: 'anthracite',
    overheadParts: 1, // Default overhead parts (1 part)
    overheadPartWindowTypes: ['fixed-sash'], // Default overhead window types
    overheadHeight: 300, // Default overhead height in mm
    parapet: 'no-parapet', // Parapet state based on numberOfParts
    parapetHeight: 250, // Default parapet height in mm
    designType: 'no-panel', // Side panel configuration
    leftPanelWidth: undefined, // Left panel width in mm
    rightPanelWidth: undefined, // Right panel width in mm
    leftPanelWindowType: 'fixed-sash', // Left panel window type
    rightPanelWindowType: 'fixed-sash', // Right panel window type
    leftPanelParapet: false, // Left panel parapet
    rightPanelParapet: false, // Right panel parapet
    leftPanelParapetHeight: 250, // Left panel parapet height in mm
    rightPanelParapetHeight: 250, // Right panel parapet height in mm
    leftPanelParapetColor: undefined, // Left panel parapet color
    rightPanelParapetColor: undefined, // Right panel parapet color
    leftPanelParapetPanelType: undefined, // Left panel parapet panel type
    rightPanelParapetPanelType: undefined, // Right panel parapet panel type
    quantity: 1
  });

  const handleConfigChange = (field, value) => {
    setConfig(prev => {
      const updates = { [field]: value };
      
      // When numberOfParts changes, update parapet accordingly
      if (field === 'numberOfParts') {
        updates.parapet = value === 'two-parts' ? 'with-parapet' : 'no-parapet';
        // When switching to two-parts, set default panelType to sandwich-no-mullions
        if (value === 'two-parts') {
          updates.panelType = 'sandwich-no-mullions';
        }
      }
      
      return {
        ...prev,
        ...updates
      };
    });
  };

  const handleOverheadPartWindowTypeChange = (index, windowType) => {
    setConfig(prev => {
      const currentParts = prev.topLight === 'with-top-light' ? (prev.overheadParts || 1) : 1;
      const currentWindowTypes = prev.overheadPartWindowTypes || Array(currentParts).fill('fixed-sash');
      const newWindowTypes = [...currentWindowTypes];
      
      // Ensure array is long enough
      while (newWindowTypes.length <= index) {
        newWindowTypes.push('fixed-sash');
      }
      
      newWindowTypes[index] = windowType;
      
      return {
        ...prev,
        overheadPartWindowTypes: newWindowTypes
      };
    });
  };

  // Convert topLight to doorType for DoorDrawingBoard
  const doorType = config.topLight === 'with-top-light' ? 'top-light' : 'normal';
  
  // Ensure parapet is synced with numberOfParts
  const parapet = config.numberOfParts === 'two-parts' ? 'with-parapet' : 'no-parapet';

  return (
    <div className="h-screen w-screen overflow-hidden bg-gray-50">
      {/* Header Bar */}
      <div className="bg-slate-800 text-white px-6 py-4 shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                navigate('/configurator-select', { replace: false });
              }}
              className="inline-flex items-center px-3 py-1.5 rounded-md border border-slate-600 text-sm text-white hover:bg-slate-700 transition-colors"
            >
              <span className="mr-1">←</span>
              {t('configurator.back')}
            </button>
            <div>
              <h1 className="text-2xl font-bold">{t('configurator.backDoorConfigurator')}</h1>
              <p className="text-sm text-slate-300 mt-0.5">{t('configurator.professionalSalesTool')}</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <button 
              onClick={() => setShowSavedQuotes(true)}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
            >
              {t('configurator.savedQuotes')}
            </button>
            <button 
              onClick={() => setShowQuote(true)}
              className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium transition-colors"
            >
              {t('configurator.exportQuote')}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex h-[calc(100vh-80px)]">
        {/* Left Panel - Options */}
        <div className="w-full lg:w-1/3 xl:w-1/4 overflow-hidden">
          <AchterdeurOptionsPanel
            config={config}
            onConfigChange={handleConfigChange}
          />
        </div>

        {/* Middle Panel - Drawing Board */}
        <div className="flex-1 p-6 overflow-hidden relative">
          <div ref={drawingBoardRef} className="h-full w-full flex items-center justify-center">
            <DoorDrawingBoard
              width={config.width}
              height={config.height}
              designType={config.designType || 'no-panel'}
              doorType={doorType}
              handlePosition={config.handlePosition}
              exteriorColor={config.exteriorColor}
              interiorColor={config.interiorColor}
              glassTexture={config.glassTexture}
              woodJoint="joint-45"
              singlePane={true}
              parapet={parapet}
              parapetHeight={config.parapetHeight}
              parapetColor={config.parapetColor}
              panelType={config.panelType}
              overheadPartWindowTypes={config.overheadPartWindowTypes || ['fixed-sash']}
              onOverheadPartWindowTypeChange={handleOverheadPartWindowTypeChange}
              overheadHeight={config.overheadHeight}
              onOverheadHeightChange={(value) => handleConfigChange('overheadHeight', value)}
              onParapetHeightChange={(value) => handleConfigChange('parapetHeight', value)}
              onWidthChange={(value) => handleConfigChange('width', value)}
              onHeightChange={(value) => handleConfigChange('height', value)}
              leftPanelWidth={config.leftPanelWidth}
              rightPanelWidth={config.rightPanelWidth}
              onLeftPanelWidthChange={(value) => handleConfigChange('leftPanelWidth', value)}
              onRightPanelWidthChange={(value) => handleConfigChange('rightPanelWidth', value)}
              leftPanelWindowType={config.leftPanelWindowType}
              rightPanelWindowType={config.rightPanelWindowType}
              onLeftPanelWindowTypeChange={(value) => handleConfigChange('leftPanelWindowType', value)}
              onRightPanelWindowTypeChange={(value) => handleConfigChange('rightPanelWindowType', value)}
              leftPanelParapet={config.leftPanelParapet}
              rightPanelParapet={config.rightPanelParapet}
              leftPanelParapetHeight={config.leftPanelParapetHeight}
              rightPanelParapetHeight={config.rightPanelParapetHeight}
              leftPanelParapetColor={config.leftPanelParapetColor}
              rightPanelParapetColor={config.rightPanelParapetColor}
              leftPanelParapetPanelType={config.leftPanelParapetPanelType}
              rightPanelParapetPanelType={config.rightPanelParapetPanelType}
              onLeftPanelParapetChange={(value) => handleConfigChange('leftPanelParapet', value)}
              onRightPanelParapetChange={(value) => handleConfigChange('rightPanelParapet', value)}
              onLeftPanelParapetHeightChange={(value) => handleConfigChange('leftPanelParapetHeight', value)}
              onRightPanelParapetHeightChange={(value) => handleConfigChange('rightPanelParapetHeight', value)}
              onLeftPanelParapetColorChange={(value) => handleConfigChange('leftPanelParapetColor', value)}
              onRightPanelParapetColorChange={(value) => handleConfigChange('rightPanelParapetColor', value)}
              onLeftPanelParapetPanelTypeChange={(value) => handleConfigChange('leftPanelParapetPanelType', value)}
              onRightPanelParapetPanelTypeChange={(value) => handleConfigChange('rightPanelParapetPanelType', value)}
            />
          </div>
          
          {/* Toggle Summary Button - Floating on right edge */}
          <button
            onClick={() => setShowSummary(!showSummary)}
            className={`absolute right-4 top-1/2 -translate-y-1/2 z-50 bg-orange-500 hover:bg-orange-600 text-white rounded-l-lg px-3 py-4 shadow-lg transition-all duration-300 flex items-center justify-center ${
              showSummary ? 'opacity-100' : 'opacity-100'
            }`}
            title={showSummary ? 'Hide Summary' : 'Show Summary'}
          >
            <svg
              className={`w-5 h-5 transition-transform duration-300 ${showSummary ? 'rotate-0' : 'rotate-180'}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
        
        {/* Right Panel - Summary (Slide in/out) */}
        <div className={`relative overflow-hidden border-l bg-white transition-all duration-300 ease-in-out ${
          showSummary ? 'w-full lg:w-1/4 xl:w-1/5' : 'w-0 border-0'
        }`}>
          <div className={`h-full p-4 transition-opacity duration-300 ${
            showSummary ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}>
            <Summary
              config={config}
              pricing={PRICING}
              profileSystems={profileSystems}
              glassTextures={glassTextures}
              glazingTypes={glazingTypes}
              colors={colors}
              raamtypes={raamtypes}
              hardware={[]}
              accessories={[]}
              woodJointOptions={woodJointOptions}
              partsOptions={[]}
              overheadOptions={[]}
              drawingBoardRef={drawingBoardRef}
              pageType="achterdeur"
              onConfigChange={handleConfigChange}
            />
          </div>
        </div>
      </div>
      
      {/* Quote Modal */}
      <Quote
        key={`quote-${config.quantity}`}
        open={showQuote}
        onOpenChange={setShowQuote}
        config={config}
        pricing={PRICING}
        profileSystems={profileSystems}
        glassTextures={glassTextures}
        glazingTypes={glazingTypes}
        colors={colors}
        raamtypes={raamtypes}
        hardware={[]}
        accessories={[]}
        woodJointOptions={woodJointOptions}
        partsOptions={[]}
        overheadOptions={[]}
        drawingBoardRef={drawingBoardRef}
        actionMarginTopRem={10.5}
      />
      
      {/* Saved Quotes Modal */}
      <SavedQuotes
        open={showSavedQuotes}
        onOpenChange={setShowSavedQuotes}
      />
    </div>
  );
};

export default Achterdeur;

